import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ShieldCheck, Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@shared/api/client";

interface AuditRow {
  id: number;
  evento: string | null;
  descripcion: string;
  entidad: string;
  entidad_id: number | null;
  usuario: string;
  cambios: Record<string, unknown> | null;
  fecha: string;
}

const EVENTO_BADGE: Record<string, string> = {
  created: "bg-emerald-50 text-emerald-700",
  updated: "bg-sky-50 text-sky-700",
  deleted: "bg-rose-50 text-rose-600",
};

export function AuditoriaPage() {
  const [page, setPage] = useState(1);
  const [subjectType, setSubjectType] = useState("");
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["settings", "audit-log", page, subjectType, desde, hasta],
    queryFn: async () =>
      (
        await api.get<{ data: AuditRow[]; total: number; per_page: number }>(
          "/settings/audit-log",
          { params: { page, subject_type: subjectType || undefined, desde: desde || undefined, hasta: hasta || undefined } },
        )
      ).data,
  });

  const totalPages = Math.max(1, Math.ceil((data?.total ?? 0) / (data?.per_page ?? 50)));

  return (
    <div className="p-8 h-full flex flex-col gap-5 min-h-0 overflow-auto">
      <header>
        <h1 className="text-3xl font-black flex items-center gap-3 text-slate-900">
          <ShieldCheck size={26} className="text-brand-700" /> Auditoría
        </h1>
        <p className="text-slate-500 mt-1">
          Registro de actividad: quién hizo qué y cuándo, en toda la aplicación.
        </p>
      </header>

      <div className="flex flex-wrap gap-3">
        <input
          className="rounded-lg border border-slate-300 px-3 py-2 text-sm w-52"
          placeholder="Entidad (Factura, User…)"
          value={subjectType}
          onChange={(e) => { setSubjectType(e.target.value); setPage(1); }}
        />
        <input type="date" className="rounded-lg border border-slate-300 px-3 py-2 text-sm" value={desde} onChange={(e) => { setDesde(e.target.value); setPage(1); }} />
        <input type="date" className="rounded-lg border border-slate-300 px-3 py-2 text-sm" value={hasta} onChange={(e) => { setHasta(e.target.value); setPage(1); }} />
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-slate-500"><Loader2 className="animate-spin" size={18} /> Cargando…</div>
      ) : (
        <>
          <div className="rounded-2xl border border-slate-200/70 bg-white overflow-auto">
            <table className="w-full min-w-[760px] text-sm">
              <thead>
                <tr className="text-left text-xs font-black uppercase tracking-wider text-slate-400 border-b border-slate-100">
                  <th className="px-4 py-3">Fecha</th>
                  <th className="px-4 py-3">Usuario</th>
                  <th className="px-4 py-3">Evento</th>
                  <th className="px-4 py-3">Entidad</th>
                  <th className="px-4 py-3">Detalle</th>
                </tr>
              </thead>
              <tbody>
                {(data?.data ?? []).map((r) => (
                  <tr key={r.id} className="border-b border-slate-50 hover:bg-slate-50/60">
                    <td className="px-4 py-2.5 whitespace-nowrap text-slate-500">
                      {new Date(r.fecha).toLocaleString("es-ES")}
                    </td>
                    <td className="px-4 py-2.5 font-bold text-slate-800">{r.usuario}</td>
                    <td className="px-4 py-2.5">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${EVENTO_BADGE[r.evento ?? ""] ?? "bg-slate-100 text-slate-500"}`}>
                        {r.evento ?? r.descripcion}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-slate-600">
                      {r.entidad}{r.entidad_id ? ` #${r.entidad_id}` : ""}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-slate-400 max-w-[340px] truncate" title={JSON.stringify(r.cambios)}>
                      {r.cambios ? JSON.stringify(r.cambios).slice(0, 120) : "—"}
                    </td>
                  </tr>
                ))}
                {!data?.data.length && (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">Sin registros para el filtro.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm font-bold text-slate-500">
              {data?.total ?? 0} registros · página {page} de {totalPages}
            </span>
            <div className="flex gap-2">
              <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="rounded-lg border border-slate-300 p-2 text-slate-600 hover:bg-slate-50 disabled:opacity-40"><ChevronLeft size={16} /></button>
              <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="rounded-lg border border-slate-300 p-2 text-slate-600 hover:bg-slate-50 disabled:opacity-40"><ChevronRight size={16} /></button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
